import React, { useMemo } from "react";
import WasslaLogo from './WasslaLogo';
import AppHeader from './AppHeader';
import { useTranslation } from 'react-i18next';

/**
 * WalletHeaderPremium - Premium wallet hero with balance + sparkline.
 *
 * The top navigation row (back button + title + history action) is now
 * rendered by the shared <AppHeader /> so it matches every other page
 * across Wassla. The body (balance block + sparkline) remains the
 * premium hero content unique to the wallet.
 */
export default function WalletHeaderPremium({
  balance = 0,
  currencyLabel,
  trend = [2, 4, 3, 6, 5, 8, 7], // sparkline values
  dir = "rtl",
  lang = "ar",
  onBack,
  onHistory,
  subtext, // Upgrade cue component
}) {
  const { t } = useTranslation();
  const isRTL = dir === "rtl" || lang?.startsWith("ar");
  const displayCurrency = currencyLabel || t('whp.coins');

  const lastUpdated = useMemo(() => {
    const now = new Date();
    return now.toLocaleTimeString(lang, {
      hour: "2-digit",
      minute: "2-digit",
    });
  }, [lang]);

  const max = Math.max(...trend);
  const min = Math.min(...trend);

  return (
    <div className="walletPro" dir={isRTL ? "rtl" : "ltr"} data-testid="wallet-header">
      {/* Shared AppHeader — gradient green matches wallet hero.
          Back button + My Wallet title + History action as rightSlot. */}
      <AppHeader
        variant="gradient"
        onBack={onBack}
        testId="wallet-header-top"
        titleSlot={
          <>
            <h1 className="app-header__title" data-testid="wallet-header-title">
              {t('whp.myWallet')}
            </h1>
            <span className="app-header__subtitle walletPro__security">
              🔒 {t('whp.encryptedSecure')}
            </span>
          </>
        }
        rightSlot={
          <button
            type="button"
            className="walletPro__ghostBtn walletPro__historyBtn"
            onClick={onHistory}
            data-testid="wallet-history-btn"
          >
            {t('whp.history')}
          </button>
        }
      />

      {/* Hero body — balance + sparkline, live under the sticky AppHeader. */}
      <div className="walletPro__heroBody">
        <div className="walletPro__bg" aria-hidden="true" />
        <div className="walletPro__glass" aria-hidden="true" />

        <div className="walletPro__body">
          <div className="walletPro__balanceBlock">
            <div className="walletPro__balanceValue" data-testid="wallet-balance">
              {balance}
              <span>{displayCurrency}</span>
            </div>
            <div className="walletPro__updated">
              {isRTL
                ? `آخر تحديث: ${lastUpdated}`
                : `Updated: ${lastUpdated}`}
            </div>
            {/* Upgrade Cue / Subtext */}
            {subtext && <div className="walletPro__subtext">{subtext}</div>}
          </div>

          <div className="walletPro__sparkline" aria-hidden="true">
            {trend.map((value, i) => {
              const height = ((value - min) / (max - min || 1)) * 40 + 8;
              return (
                <div
                  key={i}
                  style={{ height }}
                  className="walletPro__bar"
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

